import { defineStore } from 'pinia';
import {
	Constants,
	LanguageUtil,
	CountryUtil,
	GlobalSettingsModel,
	GlobalSettings,
} from '@fibl/oxs-common';
import type { RouteLocationRaw } from 'vue-router';
import type { EAV } from '@fibl/zdb-common';
import { getI18n } from '@/util/i18n';

export const useWebsiteStore = defineStore('website', {
	state: () => ({
		locale: 'de_DE' as LanguageUtil.LocaleCode,
		websiteCode: 'ADM' as Constants.WebConstant,
		countryCode: '' as CountryUtil.CountryCode,
		languages: LanguageUtil.LocalesAll,
		backRoute: null as RouteLocationRaw | null,
		globalSettings: new GlobalSettingsModel(),
		localeUpdated: false, // only true if explicitly set
	}),

	persist: {
		paths: ['locale', 'localeUpdated'],
	},

	getters: {
		countryOpts(): { value: CountryUtil.CountryCode; label: string }[] {
			const i18n = getI18n();
			return this.countryCodesSortedForAdmin.map((l) => ({
				value: l,
				label: i18n.t(`select.country.${l}`) as string,
			}));
		},
		countryCodesSortedForAdmin(): CountryUtil.CountryCode[] {
			const oxsCountryCodes = this.oxsCountries;
			return CountryUtil.CountryCodeList.slice(0).sort((a, b) => {
				let va = oxsCountryCodes.includes(a) ? 1 : 0;
				let vb = oxsCountryCodes.includes(b) ? 1 : 0;
				if (va !== vb) return vb - va;
				return a.localeCompare(b);
			});
		},
		countriesWithSubgroups(): { value: CountryUtil.CountryCode; label: string }[] {
			const i18n = getI18n();
			return this.countryCodesSortedForAdmin.filter(cc => this.oxsCountries.includes(cc) || !!this.globalSettings.country[cc]?.hasOfferGroups)
				.map((l) => ({
					value: l,
					label: i18n.t(`select.country.${l}`) as string,
				}));
		},
		oxsCountries(): CountryUtil.CountryCode[] {
			return CountryUtil.OxsMemberList;
		},
		rdbCountries(): CountryUtil.CountryCode[] {
			return CountryUtil.OxsMemberList.slice(0);
		},
		browserLocale(): Intl.Locale {
			return new Intl.Locale(this.locale.replace('_', '-'));
		},
		dateTimeFormatter(): Intl.DateTimeFormat {
			return new Intl.DateTimeFormat(this.browserLocale.toString(), {
				dateStyle: 'medium',
				timeStyle: 'medium',
			});
		},
		dateFormatter(): Intl.DateTimeFormat {
			return new Intl.DateTimeFormat(this.browserLocale.toString(), {
				dateStyle: 'medium',
			});
		},
		numberFormatter(): Intl.NumberFormat {
			return new Intl.NumberFormat(this.browserLocale.toString(), {
				compactDisplay: 'short',
			});
		},
		getOxsCountrySettings(): GlobalSettings.CountrySettings {
			return this.globalSettings.getCountrySettings(this.countryCode);
		},
		getCustomCountrySettings() {
			return (cc: CountryUtil.CountryCode): GlobalSettings.CountrySettings => {
				return this.globalSettings.getCountrySettings(cc);
			};
		},
		availableLocales(): LanguageUtil.LocaleCode[] {
			switch (this.websiteCode) {
				case Constants.WEB_OXS: {
					return LanguageUtil.getForCountry(this.countryCode);
				}
				case Constants.WEB_RDB: {
					const all = this.languages;
					const set = new Set(all.map((e) => e.substring(0, 2)));
					const best = Array.from(set).map((lang) => {
						const norm = (lang === 'en' ? 'en_GB' : `${lang}_${lang.toUpperCase()}`) as LanguageUtil.LocaleCode;
						if (all.includes(norm)) return norm;
						return all.find((e) => e.startsWith(lang))!;
					});
					return best;
				}
				default: {
					return this.languages;
				}
			}
		},
	},

	actions: {
		initLanguage() {
			const params = new URLSearchParams(window.location.search);
			const lang = params.get('setlang') as LanguageUtil.LocaleCode | null;
			if (lang && lang !== this.locale) {
				this.setUiLocale(lang);
			} else {
				this.setUiLocale(this.locale);	//init once
			}
		},
		setUiLocale(lang: LanguageUtil.LocaleCode, explicit?: boolean) {
			const i18n = getI18n();
			if (!this.languages.includes(lang)) {
				console.warn('invalid lang?', lang);
			}
			// 	throw new Error(i18n.t('stores.invalidLocalLanguage') + ': ' + lang);
			// console.log('setUiLocale', lang, new Error('stack').stack);
			i18n.locale = lang.substring(0, 2) as any;
			this.locale = lang;
			document.body.parentElement!.lang = lang.substring(0, 2);
			if (explicit) {
				this.localeUpdated = true;
			}
		},
		initSite(websiteCode: Constants.WebConstant, countryCode: CountryUtil.CountryCode) {
			this.websiteCode = websiteCode;
			this.countryCode = countryCode;
		},
		setBackRoute(b: RouteLocationRaw | null) {
			this.backRoute = b;
		},
		initSettings(settings: EAV.Entity<GlobalSettings.Settings>) {
			this.globalSettings.fromEntity(settings);
		},
		detectLanguageFromBrowser() {
			const browserLang = navigator.language;
			let locale: LanguageUtil.LocaleCode = 'en_GB';
			if (browserLang?.includes('-')) {
				//en-US
				const formattedLang = `${browserLang.split('-')[0]}_${browserLang.split('-')[1]}` as LanguageUtil.LocaleCode;
				const priority = LanguageUtil.getLanguagePriority(formattedLang, 'en_GB');
				locale = priority[0] as LanguageUtil.LocaleCode;
			} else {
				//en
				const possibleLanguages = LanguageUtil.LocalesAll.filter(
					(l) => l.substring(0, 2) === browserLang.substring(0, 2),
				);
				if (possibleLanguages.length > 0) {
					const priority = LanguageUtil.getLanguagePriority(possibleLanguages[0], 'en_GB');
					locale = priority[0] as LanguageUtil.LocaleCode;
				}
			}
			this.setUiLocale(locale);
		}
	},
});
